import { useState, useEffect, useRef } from 'react';
import { Keyboard, Platform, KeyboardEvent } from 'react-native';

/**
 * Hook to track keyboard visibility state.
 * On web, returns false — the browser handles viewport adjustments natively.
 */
export const useKeyboardVisible = (): boolean => {
  const [isKeyboardVisible, setKeyboardVisible] = useState(false);

  useEffect(() => {
    if (Platform.OS === 'web') return;

    const showListener = Keyboard.addListener(
      Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow',
      () => setKeyboardVisible(true)
    );
    const hideListener = Keyboard.addListener(
      Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide',
      () => setKeyboardVisible(false)
    );
    return () => {
      showListener.remove();
      hideListener.remove();
    };
  }, []);

  return isKeyboardVisible;
};

/**
 * Hook to track keyboard height.
 * On web, returns 0 — the browser handles viewport adjustments natively.
 */
export const useKeyboardHeight = (): number => {
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    if (Platform.OS === 'web') return;

    const showListener = Keyboard.addListener(
      Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow',
      (event: KeyboardEvent) => setKeyboardHeight(event.endCoordinates.height)
    );
    const hideListener = Keyboard.addListener(
      Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide',
      () => setKeyboardHeight(0)
    );
    return () => {
      showListener.remove();
      hideListener.remove();
    };
  }, []);

  return keyboardHeight;
};

const KEYBOARD_HEIGHT_THRESHOLD = 150;

const isInputFocused = () => {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    (el as HTMLElement).contentEditable === 'true'
  );
};

/**
 * Detects virtual keyboard visibility and height on web via the VisualViewport API.
 * Returns { visible: false, height: 0 } on native platforms.
 *
 * Uses both viewport height shrinkage AND active input focus to avoid
 * false positives from Android viewport adjustments during PWA launch.
 */
export const useWebKeyboard = (): { isWebKeyboardVisible: boolean; webKeyboardHeight: number } => {
  const [state, setState] = useState({ isWebKeyboardVisible: false, webKeyboardHeight: 0 });
  const baselineRef = useRef<number | null>(null);

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const viewport = window.visualViewport;
    if (!viewport) return;

    baselineRef.current = viewport.height;

    const handleResize = () => {
      if (viewport.height > (baselineRef.current ?? 0)) {
        baselineRef.current = viewport.height;
      }
      const baseline = baselineRef.current ?? window.innerHeight;
      const diff = baseline - viewport.height;
      const isVisible = diff > KEYBOARD_HEIGHT_THRESHOLD && isInputFocused();
      setState({ isWebKeyboardVisible: isVisible, webKeyboardHeight: isVisible ? diff : 0 });
    };

    viewport.addEventListener('resize', handleResize);
    return () => viewport.removeEventListener('resize', handleResize);
  }, []);

  return state;
};
